import type { ImagePresentation } from '@blocksuite/affine-model';
import { Bound } from '@blocksuite/global/gfx';

/**
 * Fixed model-space chrome around a Polaroid's image viewport.
 *
 * Horizontal: 2px border + 20px padding.
 * Vertical: 2px border + 19px padding + 7px gap + 48px footer.
 */
export const POLAROID_CHROME = {
  width: 22,
  height: 76,
} as const;

const presentationChrome = (presentation: ImagePresentation | undefined) =>
  presentation === 'polaroid' ? POLAROID_CHROME : { width: 0, height: 0 };

export function getImagePresentationSize(
  imageWidth: number,
  imageHeight: number,
  presentation: ImagePresentation | undefined
) {
  const chrome = presentationChrome(presentation);
  return {
    width: imageWidth + chrome.width,
    height: imageHeight + chrome.height,
  };
}

/**
 * Converts an image's outer bound while preserving the rendered image viewport
 * and its center. The Polaroid frame grows outside the image instead of taking
 * space from it.
 */
export function convertImagePresentationXYWH(
  xywh: string,
  from: ImagePresentation | undefined,
  to: ImagePresentation
) {
  if ((from ?? 'default') === to) return xywh;

  const bound = Bound.deserialize(xywh);
  const fromChrome = presentationChrome(from);
  const toChrome = presentationChrome(to);
  const width = Math.max(1, bound.w - fromChrome.width + toChrome.width);
  const height = Math.max(1, bound.h - fromChrome.height + toChrome.height);

  return Bound.fromCenter(bound.center, width, height).serialize();
}
